import {
	ClaimSolutionEnum,
	ClaimStatusEnum,
	ClaimTypeEnum,
	type OrderClaimDetailResponseDto,
	type OrderClaimUpdateDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useOrderDetailQuery } from "Order/Api/Query/useOrderDetailQuery";
import { useUpdateClaim } from "Order/Modules/Claim/Api/Mutation/updateClaimMutation";
import {
	getOrderUpdateClaimSchema,
	type OrderUpdateClaimFormModel,
} from "Order/Modules/Claim/Components/Form/Schema/orderUpdateClaimSchema";
import { ResolveModal } from "Order/Modules/Claim/Components/ResolveModal";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { transformAndSortPartners } from "Partner/Utilities/transformSortPartners";
import { BlankCard } from "Shared/Components/Cards/BlankCard";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import TextEditor from "Shared/Components/Form/CustomFormComponent/TextEditor";
import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import { zodResolver } from "@hookform/resolvers/zod";
import Check from "@mui/icons-material/Check";
import Refresh from "@mui/icons-material/Refresh";
import Save from "@mui/icons-material/Save";
import { Button, Grid2, Stack } from "@mui/material";
import DOMPurify from "dompurify";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

type UpdateClaimFormProps = {
	claim: OrderClaimDetailResponseDto;
	orderId?: number;
};

const claimResource = Resources.base.order.claim;
const selectOptionsResource = Resources.base.claim.claimSelectOptions;

export function UpdateClaimForm({ claim, orderId }: UpdateClaimFormProps) {
	const { t } = useResource();
	const [isOpenResolveModal, setIsOpenResolveModal] = useState(false);
	const { mutate: updateClaim, isPending } = useUpdateClaim(claim?.id, orderId);
	const { data: order } = useOrderDetailQuery(orderId);
	const { data: partners } = usePartnersListQuery({
		marketId: order?.market?.id,
		verticals: order?.verticalId.toString(),
	});

	const form = useForm<OrderUpdateClaimFormModel>({
		resolver: zodResolver(getOrderUpdateClaimSchema()),
		defaultValues: claim ?? {},
	});

	const {
		control,
		formState: { errors, isDirty },
	} = form;

	const openResolveModal = () => {
		setIsOpenResolveModal(true);
	};

	const reopenClaim = () => {
		updateClaim({
			status: ClaimStatusEnum.active,
		});
	};

	useEffect(() => {
		if (claim) {
			form.reset(claim);
		}
	}, [claim]);

	const { handleServerError } = useHandleServerError(form);

	const handleUpdateClaimSubmit = () => {
		handleServerError((data: OrderClaimUpdateDto) => updateClaim(data));
	};

	const sortedOptions = useMemo(() => {
		return transformAndSortPartners(partners?.data ?? [], order?.region?.name);
	}, [partners, order?.region]);

	return (
		<>
			<>
				<form>
					<Grid2 container spacing={1} size={{ xs: 12 }}>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Select
								id="type"
								name="type"
								label={t(claimResource.type)}
								variant="outlined"
								control={control}
								options={createSelectOptions(
									ClaimTypeEnum,
									t,
									selectOptionsResource.claimType,
								)}
								errors={errors}
								fullWidth
								disabled={claim.status === ClaimStatusEnum.resolved}
							/>
						</Grid2>
						{claim.status !== ClaimStatusEnum.resolved && (
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<DatePicker
									name="dateNextAction"
									label={t(claimResource.dateNextAction)}
									control={control}
									errors={errors}
								/>
							</Grid2>
						)}

						{claim.status === ClaimStatusEnum.resolved && (
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<Select
									id="final-solution"
									label={t(claimResource.finalSolution)}
									fullWidth
									options={createSelectOptions(
										ClaimSolutionEnum,
										t,
										selectOptionsResource.claimSolution,
									)}
									variant="outlined"
									name="finalSolution"
									control={control}
									errors={errors}
									disabled
								/>
							</Grid2>
						)}
						{claim.status === ClaimStatusEnum.resolved && (
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<DatePicker
									name="dateResolved"
									label={t(claimResource.dateResolved)}
									control={control}
									errors={errors}
									readOnly
									disabled
								/>
							</Grid2>
						)}
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Select
								id="partnerId"
								label={t(claimResource.partner)}
								fullWidth
								options={sortedOptions}
								variant="outlined"
								name="partnerId"
								control={control}
								errors={errors}
								groupBy="region"
								numeric
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							{claim.status === ClaimStatusEnum.resolved ? (
								<>
									<CustomFormLabel>
										{t(claimResource.description)}
									</CustomFormLabel>
									<BlankCard>
										{claim.description ? (
											<span
												// biome-ignore lint/security/noDangerouslySetInnerHtml: "none"
												dangerouslySetInnerHTML={{
													__html: DOMPurify.sanitize(
														claim.description,
													) as TrustedHTML,
												}}
											/>
										) : (
											<span>{t(claimResource.noDescription)}</span>
										)}
									</BlankCard>
								</>
							) : (
								<TextEditor
									name="description"
									label={t(claimResource.description)}
									control={control}
									errors={errors}
								/>
							)}
						</Grid2>
					</Grid2>
					<Stack direction="row" gap={1} mt={2}>
						<Button
							startIcon={<Save />}
							variant="contained"
							color="primary"
							onClick={handleUpdateClaimSubmit}
							loading={isPending}
						>
							{t(claimResource.saveButton)}
						</Button>
						{claim && claim.status !== ClaimStatusEnum.resolved && (
							<Button
								variant="outlined"
								color="success"
								startIcon={<Check />}
								onClick={() => openResolveModal()}
								disabled={isDirty}
							>
								{t(claimResource.resolve)}
							</Button>
						)}
						{claim && claim.status === ClaimStatusEnum.resolved && (
							<Button
								variant="outlined"
								color="primary"
								startIcon={<Refresh />}
								onClick={() => reopenClaim()}
							>
								{t(claimResource.reopen)}
							</Button>
						)}
					</Stack>
				</form>
				{claim && (
					<ResolveModal
						open={isOpenResolveModal}
						setOpen={setIsOpenResolveModal}
						claim={claim}
						orderId={orderId}
					/>
				)}
			</>
		</>
	);
}
